import { useState } from "react";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import {
  addToBasketRequest,
  updateHeaderBasketCount,
  triggerErrorEvent,
  triggerSuccessEvent,
  triggerClickEvent,
} from "../../__shared__/AddToBasketUtils/addToBasketUtils.ts";

import styles from "./AddToBasket.module.scss";
import { BasketRequestResponse } from "@~/queries/add-to-basket-client/types.gen.ts";
import { useVariant } from "@~/contexts/VariantContext/VariantContext.tsx";
import { Product } from "@~/types/types.ts";
import classNames from "classnames";
import { useProductCardEvent } from "@~/contexts/ProductCardEventContext/ProductCardEventContext.tsx";
import { useProductCardMetadata } from "@~/contexts/ProductCardMetadataContext/ProductCardMetadataContext.tsx";
import { productCardEvents } from "@~/contexts/ProductCardEventContext/productCardEvents";

const STATE_TEXT = {
  ACTIVE: "Add to your basket",
  ADDING: "Adding to your basket",
  SUCCESS: "Added to your basket",
};

const STATE_TEXT_SHORT = {
  ACTIVE: "Add to basket",
  ADDING: "Adding to basket",
  SUCCESS: "Added to basket",
};

type ATBState = "ACTIVE" | "ADDING" | "SUCCESS";

type Props = {
  className?: string;
  product: Product;
  longCtaText?: boolean;
  clientId?: string;
  handleMultiSku?: () => void;
};

export function AddToBasketButton({
  className,
  product,
  longCtaText = false,
  clientId,
  handleMultiSku,
}: Props) {
  const TEXT = longCtaText ? STATE_TEXT : STATE_TEXT_SHORT;

  const { variant } = useVariant();
  const dispatchEvent = useProductCardEvent();
  const { consumerId, instanceId, config } = useProductCardMetadata();
  const [addToBasketState, setAddToBasketState] = useState<ATBState>("ACTIVE");

  const handleErrors = async (resJson: BasketRequestResponse) => {
    if (resJson.errors.length) {
      triggerErrorEvent({
        res: resJson,
        variantId: variant?.id,
        skuId: product.aliases.pimProductId,
        quantity: 1,
      });
    }
    return resJson;
  };

  const handleSuccess = (resJson: BasketRequestResponse) => {
    updateHeaderBasketCount();

    setAddToBasketState("SUCCESS");
    triggerSuccessEvent({
      res: resJson,
      variantId: variant?.id,
      skuId: product.aliases.pimProductId,
      quantity: 1,
    });
  };

  const handleClick = () => {
    triggerClickEvent({
      variantId: variant?.id,
      skuId: product.aliases.pimProductId,
      quantity: 1,
    });

    if (handleMultiSku) {
      handleMultiSku();
      return;
    }

    dispatchEvent(productCardEvents.ctaAddToBasket);

    setAddToBasketState("ADDING");

    addToBasketRequest({
      variantId: variant!.id,
      clientId,
      consumerId,
      instanceId,
      productListConfig: config,
    }).then(
      (resJson) => {
        if (resJson.errors && resJson.errors.length > 0) {
          Promise.resolve(handleErrors(resJson)).then(handleSuccess);
        } else {
          handleSuccess(resJson);
        }
      },
      (resJson) =>
        Promise.resolve(handleErrors(resJson)).then(() => {
          setAddToBasketState("ACTIVE");
        }),
    );
  };

  return (
    <PrimaryButton
      onClick={handleClick}
      className={classNames(styles.button, className)}
      submitting={addToBasketState === "ADDING"}
      disabled={addToBasketState === "SUCCESS"}
      data-testid="add-to-basket"
      size="small"
      labelText={TEXT.ACTIVE}
      aria-label={`${TEXT.ACTIVE} for ${product.title}`}
    />
  );
}
